import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  COMPANY_KIND_LABELS,
  encodeMentions,
  type MentionPerson,
  type MentionSource,
  type PickCompanyDto,
} from "@klantpunt/shared";
import { IconAddressBook } from "../../app/icons";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";

/** Keuze in de lijst: collega, of bedrijf of contact uit het adresboek (met een regel eronder). */
type Suggestion = MentionPerson & { sub?: string };

/** Het woord met @ vlak voor de cursor ("@Pi", "@Jan de"); null als je niet aan het noemen bent. */
const AT_RE = /(?:^|\s)@([\p{L}\p{N}][\p{L}\p{N} .'-]{0,29})?$/u;

const norm = (s: string) => s.toLocaleLowerCase("nl").normalize("NFD").replace(/\p{M}/gu, "");

/** Zoek op begin van een woord in de naam. */
function matches<T extends MentionPerson>(people: T[], q: string, max: number): T[] {
  const n = norm(q.trim());
  return people
    .filter(
      (p) =>
        !n ||
        norm(p.name).startsWith(n) ||
        norm(p.name)
          .split(/\s+/)
          .some((w) => w.startsWith(n)),
    )
    .slice(0, max);
}

/** Bedrijven en hun contacten uit het adresboek; vaste leveranciers van het gebouw eerst. */
function useDirectoryRefs(buildingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["mention-directory", buildingId ?? null],
    queryFn: () => api<PickCompanyDto[]>(`/directory/pick${buildingId ? `?buildingId=${buildingId}` : ""}`),
    enabled,
    staleTime: 60_000,
    retry: false,
    select: (cs): Suggestion[] => {
      const companies = cs.map((c): Suggestion => ({
        id: c.id,
        name: c.name,
        kind: "company",
        sub: c.linked ? "Vaste leverancier van dit gebouw" : c.kinds.map((k) => COMPANY_KIND_LABELS[k]).join(", "),
      }));
      const people = cs.flatMap((c) =>
        c.people.map((p): Suggestion => ({ id: p.id, name: p.name, kind: "person", sub: c.name })),
      );
      return [...companies, ...people];
    },
  });
}

export function useMentionPeople(entity: MentionSource, id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["mention-people", entity, id],
    queryFn: () => api<MentionPerson[]>(`/mentions/people?entity=${entity}&id=${id}`),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Invoer voor een logboek met @-vermeldingen: typ @ en kies een collega uit de lijst (pijltjes en Enter, of
 * tikken). In het veld staat "@Naam"; bij plaatsen wordt dat `@[Naam](id)`.
 */
export function MentionComposer({
  id,
  entity,
  targetId,
  buildingId,
  busy,
  placeholder = "Opmerking toevoegen… typ @ om iemand te noemen",
  onSubmit,
}: {
  id: string;
  entity: MentionSource;
  targetId: string;
  /** Voor het adresboek: vaste leveranciers van dit gebouw bovenaan. */
  buildingId?: string;
  busy?: boolean;
  placeholder?: string;
  /** Geeft true als het gelukt is; dan wordt het veld leeggemaakt. */
  onSubmit: (body: string) => Promise<boolean>;
}) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<MentionPerson[]>([]);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [touched, setTouched] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const { data: me } = useMe();
  const people = useMentionPeople(entity, targetId, touched);
  const directory = useDirectoryRefs(buildingId, touched && hasModule(me, "directory"));
  // Collega's eerst; zodra je letters typt ook bedrijven en contacten uit het adresboek (zonder melding).
  const list: Suggestion[] =
    query == null
      ? []
      : query.trim()
        ? [...matches(people.data ?? [], query, 4), ...matches(directory.data ?? [], query, 4)]
        : matches(people.data ?? [], query, 6);
  const open = list.length > 0;
  // Geen keuzes: zeg waarom, zodat @ nooit "niets" lijkt te doen.
  // Na een spatie zonder treffer typ je gewoon verder (bijv. na een gekozen naam): dan geen melding.
  const empty =
    query == null || open || /\s/.test(query.trim())
      ? null
      : people.isPending || directory.isLoading
        ? "Laden…"
        : people.isError
          ? "Collega's ophalen lukt niet"
          : query.trim()
            ? `Niemand gevonden voor "${query.trim()}"`
            : "Geen collega's die dit kunnen zien";
  const listId = `${id}-people`;

  function readQuery(el: HTMLTextAreaElement) {
    const m = AT_RE.exec(el.value.slice(0, el.selectionStart ?? el.value.length));
    setQuery(m ? (m[1] ?? "") : null);
    setActive(0);
  }

  function pick(p: Suggestion) {
    const el = ref.current;
    if (!el) return;
    const caret = el.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const at = before.lastIndexOf("@");
    const next = `${before.slice(0, at)}@${p.name} ${text.slice(caret)}`;
    setText(next);
    setPicked((xs) => (xs.some((x) => x.id === p.id) ? xs : [...xs, { id: p.id, name: p.name, kind: p.kind }]));
    setQuery(null);
    const pos = at + p.name.length + 2;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  async function submit() {
    const body = text.trim();
    if (!body || busy) return;
    if (await onSubmit(encodeMentions(body, picked))) {
      setText("");
      setPicked([]);
    }
  }

  return (
    <form
      className="composer mention-composer"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="sr-only" htmlFor={id}>
        Opmerking
      </label>
      <div className="mention-field">
        <textarea
          ref={ref}
          id={id}
          rows={2}
          placeholder={placeholder}
          value={text}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listId}-${active}` : undefined}
          onFocus={() => setTouched(true)}
          onChange={(e) => {
            setText(e.target.value);
            readQuery(e.target);
          }}
          onClick={(e) => readQuery(e.currentTarget)}
          onBlur={() => setTimeout(() => setQuery(null), 150)}
          onKeyDown={(e) => {
            if (open) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => (a + 1) % list.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => (a - 1 + list.length) % list.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                pick(list[active]!);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setQuery(null);
                return;
              }
            }
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        {empty && (
          <p className="addrlist mention-list mention-empty muted small" role="status">
            {empty}
          </p>
        )}
        {open && (
          <ul className="addrlist mention-list" role="listbox" id={listId} aria-label="Noemen">
            {list.map((p, k) => (
              <li
                key={`${p.kind ?? "user"}-${p.id}`}
                id={`${listId}-${k}`}
                role="option"
                aria-selected={k === active}
                className={p.kind && p.kind !== "user" ? "ref" : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
              >
                <span className="mention-avatar" aria-hidden>
                  {p.kind && p.kind !== "user" ? <IconAddressBook /> : p.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="grow">
                  {p.name}
                  {p.sub && <span className="muted small block">{p.sub}</span>}
                </span>
                {p.kind && p.kind !== "user" && <span className="muted small">Adresboek</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mention-row">
        <button type="submit" className="btn small primary" disabled={busy || !text.trim()}>
          Plaatsen
        </button>
        <span className="hint">
          Typ @ voor een collega (krijgt een melding) of een bedrijf of contact uit het adresboek.
        </span>
      </div>
    </form>
  );
}
